/**
 * Abilities Service — CRUD over `role_abilities` and the user's
 * effective ability set (via `get_user_abilities` RPC).
 *
 * Build the CASL `Ability` instance with `buildAbilityFor(userId)` or
 * use the `<AbilityProvider>` + `useAbility()` hook in components.
 */
import { AbilityBuilder, createMongoAbility, type MongoAbility } from "@casl/ability";
import { supabase } from "@/integrations/supabase/client";
import {
  type AbilityRule,
  type Action,
  type Subject,
  abilityKey,
} from "@/types/abilities";

export type AppAbility = MongoAbility<[Action, Subject]>;

// ─────────────────────────────────────────────────────────────────────
// Reads
// ─────────────────────────────────────────────────────────────────────

export interface RoleAbilityRow extends AbilityRule {
  id: string;
  role_id: string;
}

export async function listRoleAbilities(): Promise<RoleAbilityRow[]> {
  const { data, error } = await supabase
    .from("role_abilities" as any)
    .select("id, role_id, subject, action, conditions, inverted");
  if (error) throw error;
  return (data ?? []) as any;
}

export async function getUserAbilities(userId: string): Promise<AbilityRule[]> {
  const { data, error } = await supabase.rpc("get_user_abilities" as any, {
    _user_id: userId,
  });
  if (error) throw error;
  return ((data ?? []) as any[]).map((r) => ({
    subject: r.subject as Subject,
    action: r.action as Action,
    conditions: r.conditions ?? null,
    inverted: Boolean(r.inverted),
  }));
}

// ─────────────────────────────────────────────────────────────────────
// Writes
// ─────────────────────────────────────────────────────────────────────

export async function setRoleAbility(
  roleId: string,
  subject: Subject,
  action: Action,
  value: boolean,
): Promise<void> {
  if (value) {
    const { error } = await supabase
      .from("role_abilities" as any)
      .insert({ role_id: roleId, subject, action, inverted: false });
    if (error && (error as any).code !== "23505") throw error; // ignore dup
  } else {
    const { error } = await supabase
      .from("role_abilities" as any)
      .delete()
      .eq("role_id", roleId)
      .eq("subject", subject)
      .eq("action", action)
      .eq("inverted", false);
    if (error) throw error;
  }
}

// ─────────────────────────────────────────────────────────────────────
// CASL builder
// ─────────────────────────────────────────────────────────────────────

export function buildAbility(rules: AbilityRule[]): AppAbility {
  const { can, cannot, build } = new AbilityBuilder<AppAbility>(createMongoAbility);
  for (const r of rules) {
    const fn = r.inverted ? cannot : can;
    if (r.conditions) fn(r.action, r.subject, r.conditions as any);
    else fn(r.action, r.subject);
  }
  return build();
}

export async function buildAbilityFor(userId: string): Promise<AppAbility> {
  const rules = await getUserAbilities(userId);
  return buildAbility(rules);
}

export const abilitiesService = {
  listRoleAbilities,
  getUserAbilities,
  setRoleAbility,
  buildAbility,
  buildAbilityFor,
  abilityKey,
};
